import { Alert, Flex, Link, Text, Tooltip } from '@tonic-one/react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataGrid,
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
} from '@tonic-one/react-data-grid';

import { useGlobalSettingStore } from '../../../models/useGlobalSettingModel';
import {
  PageInterface,
  QuarantineLogTypeEnum,
  SortInterface,
} from '../../../types/log';
import SmartTooltip from '../../Report/SmartTooltip';
import { MailPreviewContent } from '../Modals';
import {
  QuarantineItemModalName,
  QuarantineItemModalState,
} from '../Modals/interface';
import {
  quarantineConfig,
  quarantineNoNeedShowFields,
  quarantineNoNeedSortFields,
} from '../utils/constants';
import { QuarantineLogItem } from '../utils/types';
import { QuarantineActions } from './quarantineActions';
import {
  loadConfigFromLocalStorage,
  saveConfigToLocalStorage,
} from './configStorage';
import { useDataGridInfiniteScroll } from '@Utils/useDataGridInfiniteScroll';

const ROW_HEIGHT = 44;

// A missing/false is a legacy delegator item and only 'true' is Graph
const isSpodtmDelegatorFile = (row: QuarantineLogItem): boolean =>
  row.useSpodtmDelegator === 'true';

interface Props {
  logs: QuarantineLogItem[];
  type: QuarantineLogTypeEnum;
  page: PageInterface;
  total: number;
  sort: SortInterface;
  setPageStart: (start: number) => void;
  setTrigger: () => void;
  setSort: (sort: SortInterface) => void;
  isLoading?: boolean;
}

const QuarantineTable = (
  {
    logs,
    type,
    setPageStart,
    page,
    total,
    setTrigger,
    sort,
    setSort,
    isLoading,
  }: Props,
  ref: any
) => {
  const { t } = useTranslation();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const dataGridRef = ref;

  const [modalState, setModalState] = useState<QuarantineItemModalState>({
    name: QuarantineItemModalName.None,
    quarantineItems: [],
  });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [columnVisibility, setColumnVisibility] = useState(() =>
    loadConfigFromLocalStorage({}, 'columnVisibility')
  );
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  const getRowId = useCallback((row: QuarantineLogItem) => {
    return row.uniqueLogId || row.uniqueId || '';
  }, []);

  // Grey out the other source and show a banner when selection would mix sources
  const { lockedSource, hasMixedSelection } = useMemo(() => {
    let locked: boolean | null = null;
    const sources = new Set<boolean>();
    Object.keys(rowSelection).forEach((rowId) => {
      if (rowSelection[rowId]) {
        const row = logs.find((log) => getRowId(log) === rowId);
        if (row) {
          const isSpodtm = isSpodtmDelegatorFile(row);
          locked = locked === null ? isSpodtm : locked;
          sources.add(isSpodtm);
        }
      }
    });
    return { lockedSource: locked, hasMixedSelection: sources.size > 1 };
  }, [rowSelection, logs, getRowId]);

  const handleRowSelectionChange = useCallback(
    (newRowSelection: Record<string, boolean>) => {
      setRowSelection(newRowSelection);
    },
    []
  );

  const onClearSelectedRows = useCallback(() => {
    setRowSelection({});
  }, []);

  const handleToggleAllRowsSelected = useCallback(
    (value?: boolean) => {
      if (value === false) {
        setRowSelection({});
        return;
      }

      if (hasMixedSelection || logs.length === 0) {
        return;
      }

      const sourceToSelect = lockedSource ?? isSpodtmDelegatorFile(logs[0]);
      const selection = logs.reduce<Record<string, boolean>>(
        (selected, log) => {
          if (isSpodtmDelegatorFile(log) === sourceToSelect) {
            selected[getRowId(log)] = true;
          }
          return selected;
        },
        {}
      );

      setRowSelection(selection);
    },
    [getRowId, hasMixedSelection, lockedSource, logs]
  );

  const handleRowClick = useCallback((row: QuarantineLogItem) => {
    if (row.digestBlobPath) {
      setModalState({
        name: QuarantineItemModalName.ContentPreview,
        quarantineItems: [row],
      });
    }
  }, []);

  const columns = useMemo(() => {
    const ret: any[] = [];

    const cellProps = ({ row }: { row: { value: QuarantineLogItem } }) => ({
      display: 'flex',
      alignItems: 'center',
      height: ROW_HEIGHT,
      opacity:
        lockedSource !== null &&
        isSpodtmDelegatorFile(row.value) !== lockedSource
          ? 0.45
          : 1,
    });
    const checkboxColumnDef = {
      ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
      cell: (params: any) => {
        const defaultCell = DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF.cell;
        const checkbox =
          typeof defaultCell === 'function' ? defaultCell(params) : defaultCell;

        return params.row.getIsSelectable() ? (
          checkbox
        ) : (
          <Tooltip
            label={t('quarantine.tips.singleSourceSelection')}
            shouldWrapChildren
          >
            {checkbox}
          </Tooltip>
        );
      },
      cellProps: {
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF.cellProps,
        display: 'flex',
        alignItems: 'center',
        height: ROW_HEIGHT,
      },
    };
    ret.push(checkboxColumnDef);

    const typeConfig = quarantineConfig[type];
    typeConfig.showFields.forEach((field) => {
      if (quarantineNoNeedShowFields.indexOf(field) > -1) {
        return;
      }

      let column: any = null;

      if (field === 'orgName') {
        column = {
          id: 'companyGuid',
          header: <SmartTooltip>{t('log.fields.orgName')}</SmartTooltip>,
          accessorKey: 'orgName',
          width: 200,
          isSortable: true,
          cellProps,
          cell: ({ row }: { row: { value: QuarantineLogItem } }) => (
            <SmartTooltip>{row.value.orgName}</SmartTooltip>
          ),
        };
      } else if (field === 'mailLocation') {
        column = {
          id: field,
          header: <SmartTooltip>{t(`log.fields.${field}`)}</SmartTooltip>,
          accessorKey: 'mailLocation',
          width: 200,
          isSortable: false,
          cellProps,
          cell: ({ row }: { row: { value: QuarantineLogItem } }) => (
            <SmartTooltip>{row.value.mailLocation}</SmartTooltip>
          ),
        };
      } else if (field === 'fileOriginalPath') {
        column = {
          id: field,
          header: <SmartTooltip>{t(`log.fields.${field}`)}</SmartTooltip>,
          accessorKey: 'fileOriginalPath',
          width: 200,
          isSortable: false,
          cellProps,
          cell: ({ row }: { row: { value: QuarantineLogItem } }) => (
            <SmartTooltip>{row.value.fileOriginalPath}</SmartTooltip>
          ),
        };
      } else if (field === 'errorCode') {
        column = {
          id: field,
          header: <SmartTooltip>{t(`log.fields.${field}`)}</SmartTooltip>,
          accessorKey: field,
          width: 200,
          isSortable: !quarantineNoNeedSortFields.includes(field),
          cellProps,
          cell: ({ row }: { row: { value: QuarantineLogItem } }) => (
            <SmartTooltip>{(row.value as any)[field]}</SmartTooltip>
          ),
        };
      } else if (field === 'timestamp') {
        column = {
          id: field,
          header: (
            <SmartTooltip>
              {t('log.fields.timestampForQuarantine')}
            </SmartTooltip>
          ),
          accessorKey: 'timestamp',
          width: 200,
          isSortable: !quarantineNoNeedSortFields.includes(field),
          cellProps,
          cell: ({ row }: { row: { value: QuarantineLogItem } }) => {
            const { digestBlobPath, timestamp } = row.value;
            if (digestBlobPath) {
              return (
                <Link onClick={() => handleRowClick(row.value)} width="100%">
                  <SmartTooltip>{timestamp}</SmartTooltip>
                </Link>
              );
            }
            return <SmartTooltip>{timestamp}</SmartTooltip>;
          },
        };
      } else {
        if (
          field !== 'protectionMode' ||
          selectedOrgGlobalSetting.outboundMailFeatureShow
        ) {
          column = {
            id: field,
            header: <SmartTooltip>{t(`log.fields.${field}`)}</SmartTooltip>,
            accessorKey: field,
            width: 200,
            isSortable: !quarantineNoNeedSortFields.includes(field),
            cellProps,
            cell: ({ row }: { row: { value: QuarantineLogItem } }) => (
              <SmartTooltip>{(row.value as any)[field]}</SmartTooltip>
            ),
          };
        }
      }

      if (column) {
        ret.push(column);
      }
    });

    return ret;
  }, [
    type,
    t,
    handleRowClick,
    lockedSource,
    selectedOrgGlobalSetting.outboundMailFeatureShow,
  ]);

  useLayoutEffect(() => {
    if (columns.length > 0 && columnOrder.length === 0) {
      const defaultOrder = columns.map((column) => column.id);
      const storedOrder = loadConfigFromLocalStorage(
        defaultOrder,
        'columnOrder'
      );

      // Validate stored order contains all columns
      const isValidOrder = defaultOrder.every((id) => storedOrder.includes(id));
      setColumnOrder(isValidOrder ? storedOrder : defaultOrder);
    }
  }, [columns, columnOrder.length]);

  // Update modal state when row selection changes
  useEffect(() => {
    const items: QuarantineLogItem[] = [];
    Object.keys(rowSelection).forEach((rowId) => {
      if (rowSelection[rowId]) {
        const selectedRow = logs.find((log) => getRowId(log) === rowId);
        if (selectedRow) {
          items.push(selectedRow);
        }
      }
    });
    setModalState({
      name: QuarantineItemModalName.None,
      quarantineItems: items,
    });
  }, [logs, rowSelection, getRowId]);

  const sorting = useMemo(
    () => ({
      columnId: sort.field,
      direction: sort.direction as 'asc' | 'desc',
    }),
    [sort]
  );

  const handleSortingChange = useCallback(
    (newSorting: { columnId: string; direction: 'asc' | 'desc' }) => {
      if (
        sorting.columnId === 'timestamp' &&
        newSorting.columnId === undefined &&
        newSorting.direction === undefined
      ) {
        setSort({
          field: 'timestamp',
          direction: 'asc',
        });
      } else {
        setSort({
          field: newSorting.columnId ?? 'timestamp',
          direction: newSorting.direction ?? 'desc',
        });
      }
    },
    [setSort, sorting.columnId]
  );

  const handleColumnReorder = useCallback((order: string[]) => {
    setColumnOrder(order);
    saveConfigToLocalStorage(order, 'columnOrder');
  }, []);

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      setColumnVisibility(visibility);
      saveConfigToLocalStorage(visibility, 'columnVisibility');
    },
    []
  );

  const columnConfigHideIds = useMemo(
    () =>
      Object.keys(columnVisibility).filter(
        (columnId) => !columnVisibility[columnId]
      ),
    [columnVisibility]
  );

  const columnConfigNames = useMemo(
    () =>
      columns.reduce(
        (acc, column) => {
          if (column.id) {
            acc[column.id] = column.header as string;
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    [columns]
  );

  const hasNextPage = page.start * page.size < total;
  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isLoading) {
      setPageStart(page.start + 1);
    }
  }, [hasNextPage, isLoading, page.start, setPageStart]);

  useDataGridInfiniteScroll({
    dataGridRef,
    hasNextPage,
    isFetchingNextPage: isLoading || false,
    fetchNextPage,
  });

  return (
    <Flex flex="1" direction="column">
      <QuarantineActions
        total={total}
        quarantineType={type}
        quarantineItemsModalState={modalState}
        setQuarantineItemsModalState={setModalState}
        onClear={onClearSelectedRows}
        hasMixedSelection={hasMixedSelection}
        setTrigger={setTrigger}
        toggleAllRowsSelected={handleToggleAllRowsSelected}
        allRowChecked={
          logs.length > 0 &&
          logs.every((log) => Boolean(rowSelection[getRowId(log)]))
        }
        columnOrder={columnOrder}
        columnNames={columnConfigNames}
        columnHideIds={columnConfigHideIds}
        onColumnOrderChange={handleColumnReorder}
        onColumnHideIdsChange={(hideIds) => {
          const newVisibility = columnOrder.reduce(
            (acc, columnId) => ({
              ...acc,
              [columnId]: !hideIds.includes(columnId),
            }),
            {}
          );
          handleColumnVisibilityChange(newVisibility);
        }}
      />
      <Flex flex="1" direction="column" position="relative">
        {hasMixedSelection && (
          <Alert severity="warning" variant="outline">
            <Text>{t('quarantine.tips.singleSourceSelection')}</Text>
          </Alert>
        )}
        <DataGrid
          ref={dataGridRef}
          data={logs}
          columns={columns}
          getRowId={getRowId}
          isRowSelectable={({ row }: { row: { value: QuarantineLogItem } }) => {
            const rowId = getRowId(row.value);
            if (hasMixedSelection) {
              return Boolean(rowSelection[rowId]);
            }

            return (
              lockedSource === null ||
              isSpodtmDelegatorFile(row.value) === lockedSource
            );
          }}
          rowSelection={rowSelection}
          onRowSelectionChange={handleRowSelectionChange}
          isColumnSortable={true}
          autoSorting={false}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          isColumnResizable={true}
          isColumnReorderable={true}
          columnOrder={columnOrder}
          onColumnOrderChange={handleColumnReorder}
          columnVisibility={columnVisibility}
          showPagination={false}
          isLoading={isLoading}
          emptyContent={t('common.noData')}
          disableAutoResetRowSelection
        />
      </Flex>
      <MailPreviewContent
        quarantineType={type}
        modalState={modalState}
        setModalState={setModalState}
        setTrigger={setTrigger}
      />
    </Flex>
  );
};

const ForwardRefQuarantineTable = forwardRef(QuarantineTable);

export default ForwardRefQuarantineTable;
